import { Dropdown, InlineNotification } from '@carbon/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash-es';
import { formatMd } from 'components/well-page-content/utils';
import { RangeSlider } from './range-slider';
import styles from './eval-point-md-slider.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EvalPoint {
  index: number;
  mdFt: number;
  tvdFt: number;
  isValid: boolean;
}

interface EvalPointMdSliderProps {
  evalPoints: EvalPoint[];
  selectedMd: number | null;
  onChange: (md: number) => void;
}

interface QuickSelectOption {
  id: 'heel' | 'maxDepth' | 'td' | 'sliderMd';
  label: string;
  mdFt: number | null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Index of the eval point whose mdFt is closest to `target`. Returns -1 when
 * `points` is empty. Generic over any row with an `mdFt` so it can be reused
 * for `StructureGridProfileEvalPoint` and spacing-row shapes alike.
 * @param {{ mdFt: number }[]} points - Eval-point-shaped rows to search
 * @param {number} target - The MD to snap toward
 * @return {number} Index of the nearest point, or -1 if `points` is empty
 */
export function nearestEvalPointIndex<T extends { mdFt: number }>(
  points: T[],
  target: number
): number {
  if (points.length === 0) return -1;
  let bestIdx = 0;
  let bestDist = Math.abs(points[0].mdFt - target);
  for (let i = 1; i < points.length; i++) {
    const d = Math.abs(points[i].mdFt - target);
    if (d < bestDist) {
      bestDist = d;
      bestIdx = i;
    }
  }
  return bestIdx;
}

/**
 * Snap a raw MD value to the nearest eval point's mdFt.
 * @param {number} rawMd - Raw MD value from the slider
 * @param {EvalPoint[]} evalPoints - List of valid eval points to snap to
 * @return {number} The mdFt of the nearest eval point (rawMd when list empty)
 */
function snapToNearest(rawMd: number, evalPoints: EvalPoint[]): number {
  const idx = nearestEvalPointIndex(evalPoints, rawMd);
  return idx >= 0 ? evalPoints[idx].mdFt : rawMd;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function EvalPointMdSlider({
  evalPoints,
  selectedMd,
  onChange,
}: EvalPointMdSliderProps) {
  const isEmpty = evalPoints.length === 0;

  const heelPoint = evalPoints[0];
  const toePoint = evalPoints[evalPoints.length - 1];

  const maxDepthPoint = useMemo(() => {
    if (isEmpty) return undefined;
    return evalPoints.reduce<EvalPoint>(
      (best, p) => (p.tvdFt > best.tvdFt ? p : best),
      evalPoints[0]
    );
  }, [isEmpty, evalPoints]);

  const allInvalid = !isEmpty && evalPoints.every((p) => !p.isValid);

  const minMd = heelPoint ? Math.round(heelPoint.mdFt) : 0;
  const maxMd = toePoint ? Math.round(toePoint.mdFt) : 100;
  const currentMd = selectedMd ?? heelPoint?.mdFt ?? 0;

  // Track the slider's value locally during drag so the thumb and MD label
  // paint on every tick. The expensive parent onChange (which recomputes the
  // gun-barrel slice, 2D profile, etc.) is debounced so downstream consumers
  // only re-render once the user pauses dragging. On release (onCommit) we
  // flush immediately with the final value.
  const [pendingMd, setPendingMd] = useState<number>(Math.round(currentMd));
  // Read pendingMd via ref inside the resync effect so it isn't a dep. If it
  // were, every drag tick would re-run the effect against a stale currentMd
  // and yank the thumb back to the pre-drag position.
  const pendingMdRef = useRef(pendingMd);
  pendingMdRef.current = pendingMd;
  useEffect(() => {
    const rounded = Math.round(currentMd);
    // When the parent-driven value echoes our own committed snap, don't
    // clobber pendingMd. Only resync on true external changes (dropdown
    // pick, initial load, data reset, new eval points from BE).
    if (!isEmpty && snapToNearest(pendingMdRef.current, evalPoints) === rounded)
      return;
    setPendingMd(rounded);
  }, [currentMd, isEmpty, evalPoints]);

  // The parent onChange triggers heavy downstream re-renders (gun barrel chart,
  // 2D profile, Mapbox layer, eval-points table). Debouncing the commit so it
  // only fires after the user pauses (or releases) the slider keeps the input
  // → paint path off the heavy work and dramatically improves INP. Visual
  // feedback during drag is handled separately via pendingMd above.
  const commitToParent = useCallback(
    (val: number) => {
      if (!isEmpty) onChange(snapToNearest(val, evalPoints));
    },
    [isEmpty, onChange, evalPoints]
  );

  const debouncedCommitToParent = useMemo(
    () => debounce(commitToParent, 150),
    [commitToParent]
  );

  // Cancel any pending debounced commit on unmount or when the debounced
  // function instance changes (e.g. evalPoints/onChange identity changes).
  useEffect(() => {
    return () => debouncedCommitToParent.cancel();
  }, [debouncedCommitToParent]);

  const handleSliderChange = useCallback(
    (val: number) => {
      setPendingMd(val);
      debouncedCommitToParent(val);
    },
    [debouncedCommitToParent]
  );

  const handleSliderCommit = useCallback(
    (val: number) => {
      debouncedCommitToParent.cancel();
      commitToParent(val);
    },
    [debouncedCommitToParent, commitToParent]
  );

  const sliderValue = isEmpty ? minMd : pendingMd;
  const displayMd = isEmpty ? currentMd : pendingMd;

  const quickSelectOptions: QuickSelectOption[] = useMemo(() => {
    if (isEmpty || !heelPoint || !maxDepthPoint || !toePoint) return [];
    return [
      {
        id: 'heel',
        label: `Heel (${formatMd(heelPoint.mdFt)})`,
        mdFt: heelPoint.mdFt,
      },
      {
        id: 'maxDepth',
        label: `Max Depth (${formatMd(maxDepthPoint.mdFt)})`,
        mdFt: maxDepthPoint.mdFt,
      },
      {
        id: 'td',
        label: `TD (${formatMd(toePoint.mdFt)})`,
        mdFt: toePoint.mdFt,
      },
      { id: 'sliderMd', label: 'Slider MD', mdFt: null },
    ];
  }, [isEmpty, heelPoint, maxDepthPoint, toePoint]);

  const selectedDropdownItem =
    quickSelectOptions.find(
      (opt) => opt.mdFt !== null && opt.mdFt === currentMd
    ) ??
    quickSelectOptions.find((opt) => opt.id === 'sliderMd') ??
    null;

  return (
    <div className={styles.container}>
      {allInvalid && (
        <InlineNotification
          kind="warning"
          title="No valid evaluation points available."
          hideCloseButton
          className={styles.invalidBanner}
        />
      )}

      {/* Quick-select: Heel / Max Depth / TD / Slider MD */}
      <div className={styles.dropdownRow}>
        <span className={styles.label}>Reference Point</span>
        <Dropdown
          id="eval-point-quick-select"
          titleText=""
          label="Select a reference point"
          items={quickSelectOptions}
          itemToString={(item) => item?.label ?? ''}
          selectedItem={selectedDropdownItem}
          onChange={({ selectedItem }) => {
            if (selectedItem && selectedItem.mdFt !== null) {
              onChange(selectedItem.mdFt);
            }
          }}
          size="sm"
          className={styles.dropdown}
          disabled={isEmpty}
        />
      </div>

      {/* Fine-grained MD slider */}
      <div className={styles.sliderSection}>
        <span className={styles.sliderSectionLabel}>Reference Point (MD)</span>
        <RangeSlider
          min={minMd}
          max={maxMd}
          value={sliderValue}
          onChange={handleSliderChange}
          onCommit={handleSliderCommit}
          minLabel={heelPoint ? formatMd(heelPoint.mdFt) : ''}
          maxLabel={toePoint ? formatMd(toePoint.mdFt) : ''}
          disabled={isEmpty}
        />
        <div className={styles.selectedValue}>{formatMd(displayMd)}</div>
      </div>
    </div>
  );
}
